import { createContext, useContext, useState, useCallback, type ReactNode } from "react"

export interface ChatVisibilityContextType {
  isChatOpen: boolean
  toggleChat: () => void
  openChat: () => void
  closeChat: () => void
}

const ChatVisibilityContext = createContext<ChatVisibilityContextType | undefined>(undefined)

interface ChatVisibilityProviderProps {
  children: ReactNode
  defaultOpen?: boolean
}

export function ChatVisibilityProvider({ 
  children, 
  defaultOpen = false 
}: ChatVisibilityProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(defaultOpen)

  const toggleChat = useCallback(() => {
    setIsChatOpen(prev => !prev)
  }, [])

  const openChat = useCallback(() => {
    setIsChatOpen(true)
  }, [])

  const closeChat = useCallback(() => {
    setIsChatOpen(false)
  }, [])

  const value: ChatVisibilityContextType = {
    isChatOpen,
    toggleChat,
    openChat,
    closeChat
  }

  return (
    <ChatVisibilityContext.Provider value={value}>
      {children}
    </ChatVisibilityContext.Provider>
  )
}

export function useChatVisibility() {
  const context = useContext(ChatVisibilityContext)
  if (context === undefined) {
    throw new Error("useChatVisibility must be used within a ChatVisibilityProvider")
  }
  return context
} 